import { clientConfig } from '@/config/clientConfig';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { useCan } from '@skewb/access-control/react';
import { Edit, Mail, MapPin, Phone, Plus, Shield, User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { SectionCard } from '../components/SectionCard';
import { UserRoleBadges } from '../components/UserRoleBadges';
import { UserStatsCards } from '../components/UserStatsCards';
import { AssignRoleDialog } from '../components/dialogs/AssignRoleDialog';
import { EditAddressDialog } from '../components/dialogs/EditAddressDialog';
import type { ColorScheme, User, UserDetail } from '../types/user';

interface PersonalDetailsSectionProps {
  user: User;
  colorScheme: ColorScheme;
  userId?: string;
  userDetail?: UserDetail;
}

export function PersonalDetailsSection({
  user,
  colorScheme,
  userId,
  userDetail,
}: PersonalDetailsSectionProps) {
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = useState(false);

  const canEdit = !!userId;
  // Editing address/personal details hits `update User` — a separate capability from role
  // management below, so the address Edit affordance is hidden when the actor lacks it
  // without affecting the role section (WRK-5474).
  const canUpdateUser = useCan('update', 'User');
  const canManageRoles = useCan('create', 'AccessControl');
  const phoneHref = user.phone ? `tel:${user.phone.replace(/\s+/g, '')}` : undefined;
  const emailHref = user.email ? `mailto:${user.email}` : undefined;

  return (
    <div className="max-w-6xl space-y-6">
      <UserStatsCards user={user} colorScheme={colorScheme} />

      <CollapsibleCard
        title="Basic Information"
        subtitle="Core personal details and identification"
        icon={UserIcon}
        iconBg="bg-gray-100 dark:bg-gray-900/30"
        iconColor="text-gray-600 dark:text-gray-400"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Full Name
            </label>
            <div className="text-sm text-foreground font-medium">{user.name}</div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Date of Birth
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.dateOfBirth?.slice(0, 10) || 'Not provided'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Gender
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.gender || 'Not provided'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Account Status
            </label>
            <div>
              <Badge
                variant="outline"
                className={
                  user.status === 'offline'
                    ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                    : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-300 dark:border-green-700'
                }
              >
                {user.status === 'offline' ? 'Inactive' : 'Active'}
              </Badge>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Contact Information"
        subtitle="How to reach this user"
        icon={Mail}
        iconBg="bg-green-100 dark:bg-green-900/30"
        iconColor="text-green-600 dark:text-green-400"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="space-y-1 flex-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Phone Number
              </label>
              <div className="text-sm text-foreground font-medium">
                {user.phone || 'Not provided'}
              </div>
              {clientConfig.features.callNow && phoneHref && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs text-orange-600 dark:text-orange-400"
                  asChild
                >
                  <a href={phoneHref}>Call Now</a>
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="space-y-1 flex-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Email Address
              </label>
              <div className="text-sm text-foreground font-medium break-all">
                {user.email || 'Not provided'}
              </div>
              {emailHref && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs text-orange-600 dark:text-orange-400"
                  asChild
                >
                  <a href={emailHref}>Send Email</a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Address Details"
        subtitle="Residential address information"
        icon={MapPin}
        iconBg="bg-amber-100 dark:bg-amber-900/30"
        iconColor="text-amber-600 dark:text-amber-400"
        actions={
          canEdit && canUpdateUser ? (
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={() => setEditAddressOpen(true)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : null
        }
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { label: 'Address Line 1', value: userDetail?.addressLine1 },
            { label: 'Address Line 2', value: userDetail?.addressLine2 },
            { label: 'City/Town', value: userDetail?.addressLine3 },
            { label: 'Postcode', value: userDetail?.postcode },
          ].map(({ label, value }) => (
            <div key={label} className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {label}
              </label>
              <div
                className={`text-sm font-medium ${
                  value ? 'text-foreground' : 'text-muted-foreground italic'
                }`}
              >
                {value || 'Not provided'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-dashed border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>Complete address information for accurate record keeping</span>
          </div>
        </div>
      </CollapsibleCard>

      <SectionCard
        title="System Roles & Permissions"
        subtitle="Access control and role assignments"
        icon={Shield}
        iconBg="bg-purple-100 dark:bg-purple-900/30"
        iconColor="text-purple-600 dark:text-purple-400"
        actions={
          canManageRoles && (
            <Button
              variant="outline"
              size="sm"
              className="border-border text-orange-600 dark:text-orange-400"
              disabled={!canEdit}
              onClick={() => setAssignRoleOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Assign Role
            </Button>
          )
        }
      >
        {canEdit && userId ? (
          <UserRoleBadges userId={Number(userId)} />
        ) : (
          <span className="text-sm text-muted-foreground">No roles assigned</span>
        )}
      </SectionCard>

      {canEdit && userId && (
        <>
          {canUpdateUser && (
            <EditAddressDialog
              open={editAddressOpen}
              onOpenChange={setEditAddressOpen}
              userId={userId}
              user={userDetail}
            />
          )}
          {canManageRoles && (
            <AssignRoleDialog
              open={assignRoleOpen}
              onOpenChange={setAssignRoleOpen}
              userId={userId}
            />
          )}
        </>
      )}
    </div>
  );
}
